import { CalendarDays } from 'lucide-react';
import React from 'react';

import {
    type MessageShiftFootnote,
    MessageShiftFootnotes,
} from '@apps/web/components/shared/messages/message_shift_footnotes';
import { Button } from '@apps/web/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

// Only the active panel participates; opening another never notifies unrelated rows.
let closeActiveShiftPanel: (() => void) | null = null;

type MessageShiftActionsProps = {
    readonly children: React.ReactNode;
    readonly footnotes?: ReadonlyArray<MessageShiftFootnote> | null;
    readonly timezone?: string;
    readonly isInbound: boolean;
};

const MessageShiftActionsContext = React.createContext<{
    readonly footnotes: ReadonlyArray<MessageShiftFootnote>;
    readonly timezone?: string;
    readonly isInbound: boolean;
    readonly open: boolean;
    readonly close: () => void;
    readonly toggle: () => void;
    readonly triggerRef: React.RefObject<HTMLButtonElement | null>;
    readonly panelRef: React.RefObject<HTMLDivElement | null>;
} | null>(null);

function useMessageShiftActions() {
    const context = React.useContext(MessageShiftActionsContext);
    if (!context) {
        throw new Error(
            'Message shift action slots require MessageShiftActions',
        );
    }
    return context;
}

export function MessageShiftActions({
    children,
    footnotes,
    timezone,
    isInbound,
}: MessageShiftActionsProps) {
    const [open, setOpen] = React.useState(false);
    const close = React.useCallback(function closePanel() {
        if (closeActiveShiftPanel === closePanel) {
            closeActiveShiftPanel = null;
        }
        setOpen(false);
    }, []);
    function toggle() {
        if (closeActiveShiftPanel === close) {
            close();
            return;
        }
        closeActiveShiftPanel?.();
        closeActiveShiftPanel = close;
        setOpen(true);
    }
    React.useEffect(
        () => () => {
            if (closeActiveShiftPanel === close) {
                closeActiveShiftPanel = null;
            }
        },
        [close],
    );
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        function closeOutside(event: PointerEvent) {
            if (
                event.target instanceof Node &&
                !triggerRef.current?.contains(event.target) &&
                !panelRef.current?.contains(event.target)
            ) {
                close();
            }
        }
        function closeOnEscape(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                close();
                triggerRef.current?.focus();
            }
        }
        document.addEventListener('pointerdown', closeOutside);
        // Handle Escape before enclosing dialogs receive it on document.
        window.addEventListener('keydown', closeOnEscape, true);
        return () => {
            document.removeEventListener('pointerdown', closeOutside);
            window.removeEventListener('keydown', closeOnEscape, true);
        };
    }, [open, close]);

    return (
        <MessageShiftActionsContext.Provider
            value={{
                footnotes: footnotes ?? [],
                timezone,
                isInbound,
                open,
                close,
                toggle,
                triggerRef,
                panelRef,
            }}
        >
            <div>{children}</div>
        </MessageShiftActionsContext.Provider>
    );
}

export function MessageShiftActionsTrigger() {
    const { footnotes, open, toggle, triggerRef } = useMessageShiftActions();
    if (!footnotes.length) {
        return null;
    }
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Button
                    aria-label="Show shifts in this message"
                    aria-expanded={open}
                    className="text-muted-foreground hover:text-foreground size-8"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggle();
                    }}
                    ref={triggerRef}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <CalendarDays className="size-4" />
                </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6}>
                Shifts
            </TooltipContent>
        </Tooltip>
    );
}

export function MessageShiftActionsPanel() {
    const { footnotes, timezone, isInbound, open, close, panelRef } =
        useMessageShiftActions();
    if (!open || !footnotes.length) {
        return null;
    }
    return (
        <div
            className={cn(
                'flex',
                isInbound ? 'justify-start pl-10' : 'justify-end pr-10',
            )}
        >
            <div
                className="w-full max-w-[680px] min-w-0"
                ref={panelRef}
                // Keep panel controls from navigating a wrapping message link.
                onClick={(event) => {
                    // Preserve deliberate row-link navigation, including modified clicks.
                    const link =
                        event.target instanceof Element
                            ? event.target.closest('a[href]')
                            : null;
                    if (!link || !event.currentTarget.contains(link)) {
                        event.preventDefault();
                    }
                    event.stopPropagation();
                }}
            >
                <MessageShiftFootnotes
                    footnotes={footnotes}
                    onClose={close}
                    timezone={timezone}
                />
            </div>
        </div>
    );
}
